'use client';

import { useTranslations } from 'next-intl';
import { VStack } from '@terra/ui-styled-system';
import {
  FindingSeveritySchema,
  type FindingSeverityType,
} from '@terra/shared-schemas-orchestrator';
import { isSeverityReasonMissing } from './finding-severity-reason';
import { Field } from '../../components/field';
import { Select, createSelectListCollection } from '../../components/select';
import { Textarea } from '../../components/textarea';

const severityOptions = createSelectListCollection({
  items: FindingSeveritySchema.options.map((severity) => ({
    label: severity,
    value: severity,
  })),
});

export type FindingSeverityReasonFieldProps = {
  severity: FindingSeverityType;
  originalSeverity: FindingSeverityType | undefined;
  severityReason: string | undefined;
  onSeverityChange: (severity: FindingSeverityType) => void;
  onSeverityReasonChange: (reason: string | undefined) => void;
};

export function FindingSeverityReasonField({
  severity,
  originalSeverity,
  severityReason,
  onSeverityChange,
  onSeverityReasonChange,
}: FindingSeverityReasonFieldProps) {
  const t = useTranslations('designSystem.findings.cvssVersions');
  const isChanged =
    originalSeverity !== undefined && severity !== originalSeverity;
  const reasonMissing = isSeverityReasonMissing({
    severity,
    originalSeverity,
    severityReason,
  });
  const handleSeverityChange = ({ value }: { value: string[] }) => {
    const parsed = FindingSeveritySchema.safeParse(value[0]);

    if (!parsed.success) {
      return;
    }

    onSeverityChange(parsed.data);

    if (parsed.data === originalSeverity) {
      onSeverityReasonChange(undefined);
    }
  };

  return (
    <VStack gap={3} alignItems="stretch" w="full">
      <Field.Root>
        <Field.Label fontSize="xs" fontWeight="medium">
          {t('severityLabel')}
        </Field.Label>
        <Select.Root
          positioning={{ sameWidth: true, strategy: 'fixed' }}
          size="sm"
          collection={severityOptions}
          value={[severity]}
          onValueChange={handleSeverityChange}
        >
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText />
            </Select.Trigger>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              <Select.ItemGroup>
                {severityOptions.items.map((item) => (
                  <Select.Item key={item.value} item={item}>
                    <Select.ItemText>{item.label}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.ItemGroup>
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      </Field.Root>
      {isChanged ? (
        <Field.Root required invalid={reasonMissing}>
          <Field.Label
            fontSize="xs"
            fontWeight="medium"
            htmlFor="severity-reason"
          >
            {t('severityReasonLabel')}
          </Field.Label>
          <Textarea
            id="severity-reason"
            aria-label={t('severityReasonLabel')}
            value={severityReason ?? ''}
            onChange={(event) => onSeverityReasonChange(event.target.value)}
          />
          {reasonMissing ? (
            <Field.ErrorText>{t('severityReasonMissing')}</Field.ErrorText>
          ) : (
            <Field.HelperText fontSize="2xs" color="fg.muted">
              {t('severityReasonHelper')}
            </Field.HelperText>
          )}
        </Field.Root>
      ) : null}
    </VStack>
  );
}
